import React, { useState, useEffect, useContext, useCallback } from "react";
import { toast } from "react-toastify";

import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import PeopleIcon from "@material-ui/icons/People";
import DownloadIcon from "@material-ui/icons/GetApp";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactListDialog from "../../components/ContactListDialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { Grid } from "@material-ui/core";

import planilhaExemplo from "../../assets/planilha.xlsx";
import { SocketContext } from "../../context/Socket/SocketContext";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    padding: theme.spacing(1),
    ...theme.scrollbarStyles,
  },
  tableWrapper: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
  },
  paginationWrapper: {
    borderTop: "1px solid rgba(224, 224, 224, 1)",
  },
}));

const ContactLists = () => {
  const classes = useStyles();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [count, setCount] = useState(0);
  const [selectedContactList, setSelectedContactList] = useState(null);
  const [deletingContactList, setDeletingContactList] = useState(null);
  const [contactListModalOpen, setContactListModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [contactLists, setContactLists] = useState([]);

  const socketManager = useContext(SocketContext);

  const fetchContactLists = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }

    try {
      const { data } = await api.get("/contact-lists/", {
        params: {
          searchParam,
          pageNumber: pageNumber + 1,
          pageSize: rowsPerPage,
        },
      });

      setContactLists(data.records);
      setCount(Number(data.count) || 0);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [pageNumber, rowsPerPage, searchParam]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchContactLists();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [fetchContactLists]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    const handleContactListEvent = () => {
      fetchContactLists(false);
    };

    socket.on(`company-${companyId}-ContactList`, handleContactListEvent);

    return () => {
      socket.off(`company-${companyId}-ContactList`, handleContactListEvent);
    };
  }, [fetchContactLists, socketManager]);

  const handleOpenContactListModal = () => {
    setSelectedContactList(null);
    setContactListModalOpen(true);
  };

  const handleCloseContactListModal = () => {
    setSelectedContactList(null);
    setContactListModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
    setPageNumber(0);
  };

  const handleEditContactList = (contactList) => {
    setSelectedContactList(contactList);
    setContactListModalOpen(true);
  };

  const handleDeleteContactList = async (contactListId) => {
    let shouldGoBackOnePage = false;

    try {
      await api.delete(`/contact-lists/${contactListId}`);
      shouldGoBackOnePage = pageNumber > 0 && contactLists.length === 1;
      toast.success(i18n.t("contactLists.toasts.deleted"));
    } catch (err) {
      toastError(err);
      return;
    }

    setDeletingContactList(null);
    if (shouldGoBackOnePage) {
      setPageNumber((prevState) => prevState - 1);
    } else {
      fetchContactLists(false);
    }
  };

  const handleChangePage = (_event, newPage) => {
    setPageNumber(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPageNumber(0);
  };

  const goToContacts = (id) => {
    history.push(`/contact-lists/${id}/contacts`);
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingContactList &&
          `${i18n.t("contactLists.confirmationModal.deleteTitle")} ${
            deletingContactList.name
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteContactList(deletingContactList.id)}
      >
        {i18n.t("contactLists.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <ContactListDialog
        open={contactListModalOpen}
        onClose={handleCloseContactListModal}
        aria-labelledby="form-dialog-title"
        contactListId={selectedContactList && selectedContactList.id}
      />
      <MainHeader>
        <Grid style={{ width: "99.6%" }} container>
          <Grid xs={12} sm={8} item>
            <Title>{i18n.t("contactLists.title")}</Title>
          </Grid>
          <Grid xs={12} sm={4} item>
            <Grid spacing={2} container>
              <Grid xs={7} sm={6} item>
                <TextField
                  fullWidth
                  placeholder={i18n.t("contactLists.searchPlaceholder")}
                  type="search"
                  value={searchParam}
                  onChange={handleSearch}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ color: "gray" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid xs={5} sm={6} item>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={handleOpenContactListModal}
                >
                  {i18n.t("contactLists.buttons.add")}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MainHeader>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
      >
        {/* Alterado por GitHub Copilot em 2026-04-15: paginação explícita para suportar grandes volumes de listas no CRUD. */}
        <div className={classes.tableWrapper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="center">ID</TableCell>
                <TableCell align="center">
                  {i18n.t("contactLists.table.name")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("contactLists.table.contacts")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("contactLists.table.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <>
                {contactLists.map((contactList) => (
                  <TableRow key={contactList.id}>
                    <TableCell align="center">{contactList.id}</TableCell>
                    <TableCell align="center">{contactList.name}</TableCell>
                    <TableCell align="center">
                      {contactList.contactsCount || 0}
                    </TableCell>
                    <TableCell align="center">
                      <a href={planilhaExemplo} download="planilha.xlsx">
                        <IconButton
                          size="small"
                          title={i18n.t("contactLists.buttons.downloadSample")}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </a>

                      <IconButton
                        size="small"
                        onClick={() => goToContacts(contactList.id)}
                      >
                        <PeopleIcon />
                      </IconButton>

                      <IconButton
                        size="small"
                        onClick={() => handleEditContactList(contactList)}
                      >
                        <EditIcon />
                      </IconButton>

                      <IconButton
                        size="small"
                        onClick={() => {
                          setConfirmModalOpen(true);
                          setDeletingContactList(contactList);
                        }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {loading && <TableRowSkeleton columns={4} />}
              </>
            </TableBody>
          </Table>
        </div>
        <div className={classes.paginationWrapper}>
          <TablePagination
            component="div"
            count={count}
            page={pageNumber}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[20, 50, 100]}
            labelRowsPerPage={i18n.t("contactLists.pagination.rowsPerPage")}
            labelDisplayedRows={({ from, to, count: total }) =>
              i18n.t("contactLists.pagination.displayedRows", {
                from,
                to,
                total: total !== -1 ? total : to,
              })
            }
          />
        </div>
      </Paper>
    </MainContainer>
  );
};

export default ContactLists;
